import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Mail } from 'lucide-react';

const TermsConditionsPage = () => {
  const lastUpdated = "January 3, 2026";

  return (
    <div data-testid="terms-conditions-page" className="page-transition">
      {/* Hero Section */}
      <section className="pt-40 pb-16 bg-[#050505] relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#111111] to-[#050505]" />
        <div className="container-custom relative z-10">
          <Link to="/" className="inline-flex items-center gap-2 text-[#D4AF37] hover:text-[#C5A059] transition-colors mb-8">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
          <h1 className="text-4xl md:text-5xl font-bold text-[#F5F5F7] mb-4">
            Terms & Conditions
          </h1>
          <p className="text-[#71717A]">Last Updated: {lastUpdated}</p>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16 bg-[#050505]">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <div className="prose prose-invert max-w-none">
              
              {/* Introduction */}
              <div className="mb-12">
                <p className="text-[#A1A1AA] leading-relaxed text-lg">
                  Welcome to Eleven11 Studios. These Terms and Conditions ("Terms") govern your use of our website 
                  www.eleven11-studios.com and the services provided by Eleven11 Studios ("we," "our," or "us"), 
                  a premium web design and development studio serving businesses in the USA and Canada.
                </p>
                <p className="text-[#A1A1AA] leading-relaxed text-lg mt-4">
                  By accessing our website or engaging our services, you agree to be bound by these Terms. 
                  If you do not agree, please do not use our services.
                </p>
              </div>

              {/* Section 1 */}
              <div className="mb-10">
                <h2 className="text-2xl font-bold text-[#F5F5F7] mb-4">1. Services Offered</h2>
                <p className="text-[#A1A1AA] leading-relaxed mb-4">
                  Eleven11 Studios provides the following web design and development services:
                </p>
                <ul className="list-disc list-inside text-[#A1A1AA] space-y-2 ml-4">
                  <li><strong className="text-[#F5F5F7]">Custom Website Development:</strong> Bespoke website design and development tailored to your business needs</li>
                  <li><strong className="text-[#F5F5F7]">Landing Page Development:</strong> High-converting landing pages optimized for user experience</li>
                  <li><strong className="text-[#F5F5F7]">E-Commerce Development:</strong> Online store development with product management and checkout systems</li>
                  <li><strong className="text-[#F5F5F7]">UI/UX Design:</strong> User interface and user experience design services</li>
                  <li><strong className="text-[#F5F5F7]">Website Optimization:</strong> Performance optimization, speed improvements, and user experience enhancements</li>
                  <li><strong className="text-[#F5F5F7]">Website Deployment & Maintenance:</strong> Hosting setup, deployment, ongoing maintenance, and technical support</li>
                </ul>
              </div>

              {/* Section 2 */}
              <div className="mb-10">
                <h2 className="text-2xl font-bold text-[#F5F5F7] mb-4">2. Digital Service Delivery</h2>
                <div className="bg-[#111111] border border-white/10 p-6 mb-4">
                  <p className="text-[#F5F5F7]">
                    All services provided by Eleven11 Studios are delivered digitally. There are no physical products 
                    or in-person deliverables unless specifically agreed upon in writing.
                  </p>
                </div>
                <p className="text-[#A1A1AA] leading-relaxed">
                  Digital deliverables may include but are not limited to: website files, design assets, source code, 
                  design mockups, and access credentials to hosting platforms and content management systems.
                </p>
              </div>

              {/* Section 3 */}
              <div className="mb-10">
                <h2 className="text-2xl font-bold text-[#F5F5F7] mb-4">3. Project Timelines</h2>
                <p className="text-[#A1A1AA] leading-relaxed mb-4">
                  Typical project timelines are as follows:
                </p>
                <ul className="list-disc list-inside text-[#A1A1AA] space-y-2 ml-4">
                  <li><strong className="text-[#F5F5F7]">Landing Pages:</strong> 2-3 weeks</li>
                  <li><strong className="text-[#F5F5F7]">Custom Websites:</strong> 4-8 weeks</li>
                  <li><strong className="text-[#F5F5F7]">E-Commerce Websites:</strong> 6-12 weeks</li>
                  <li><strong className="text-[#F5F5F7]">Website Optimization:</strong> 1-2 weeks</li>
                  <li><strong className="text-[#F5F5F7]">Deployment & Maintenance:</strong> Ongoing (monthly/annual plans)</li>
                </ul>
                <p className="text-[#A1A1AA] leading-relaxed mt-4">
                  Actual completion times depend on project complexity, timeliness of client feedback, and availability 
                  of required content and assets from the client. Delays caused by the client may result in extended 
                  timelines without liability on our part.
                </p>
              </div>

              {/* Section 4 */}
              <div className="mb-10">
                <h2 className="text-2xl font-bold text-[#F5F5F7] mb-4">4. Client Responsibilities</h2>
                <p className="text-[#A1A1AA] leading-relaxed mb-4">
                  By engaging our services, you agree to:
                </p>
                <ul className="list-disc list-inside text-[#A1A1AA] space-y-2 ml-4">
                  <li>Provide accurate and complete information as requested</li>
                  <li>Grant necessary access to hosting, domains, and tools required for service delivery</li>
                  <li>Respond to requests for feedback and approvals in a timely manner</li>
                  <li>Ensure all content provided (text, images, logos) is owned by you or properly licensed</li>
                  <li>Make payments according to the agreed schedule</li>
                </ul>
              </div>

              {/* Section 5 */}
              <div className="mb-10">
                <h2 className="text-2xl font-bold text-[#F5F5F7] mb-4">5. Payment Terms</h2>
                <p className="text-[#A1A1AA] leading-relaxed mb-4">
                  Payment terms will be outlined in your service agreement or invoice. General terms include:
                </p>
                <ul className="list-disc list-inside text-[#A1A1AA] space-y-2 ml-4">
                  <li>A deposit (typically 50%) is required before work begins</li>
                  <li>Final payment is due upon project completion before handover</li>
                  <li>Late payments may result in project suspension</li>
                  <li>All prices are in USD unless otherwise specified</li>
                </ul>
              </div>

              {/* Section 6 */}
              <div className="mb-10">
                <h2 className="text-2xl font-bold text-[#F5F5F7] mb-4">6. Intellectual Property</h2>
                <p className="text-[#A1A1AA] leading-relaxed mb-4">
                  Upon full payment:
                </p>
                <ul className="list-disc list-inside text-[#A1A1AA] space-y-2 ml-4">
                  <li>You own the final website and design deliverables created specifically for your project</li>
                  <li>We retain the right to showcase work samples in our portfolio (unless otherwise agreed)</li>
                  <li>Third-party assets (stock photos, fonts, plugins) remain subject to their respective licenses</li>
                </ul>
                <p className="text-[#A1A1AA] leading-relaxed mt-4">
                  Our proprietary tools, processes, and development methodologies remain the intellectual property of Eleven11 Studios.
                </p>
              </div>

              {/* Section 7 */}
              <div className="mb-10">
                <h2 className="text-2xl font-bold text-[#F5F5F7] mb-4">7. Limitation of Liability</h2>
                <div className="bg-[#111111] border border-[#D4AF37]/30 p-6 mb-4">
                  <p className="text-[#A1A1AA] leading-relaxed">
                    <strong className="text-[#F5F5F7]">To the maximum extent permitted by law:</strong> Eleven11 Studios shall not be liable 
                    for any indirect, incidental, special, consequential, or punitive damages, including but not limited to 
                    loss of profits, revenue, data, or business opportunities, arising from or related to our services.
                  </p>
                </div>
                <p className="text-[#A1A1AA] leading-relaxed">
                  Our total liability for any claim shall not exceed the amount paid by you for the specific service giving rise to the claim.
                </p>
              </div>

              {/* Section 8 */}
              <div className="mb-10">
                <h2 className="text-2xl font-bold text-[#F5F5F7] mb-4">8. Prohibited Uses</h2>
                <p className="text-[#A1A1AA] leading-relaxed mb-4">
                  You agree NOT to use our services for:
                </p>
                <ul className="list-disc list-inside text-[#A1A1AA] space-y-2 ml-4">
                  <li>Any illegal, fraudulent, or harmful activities</li>
                  <li>Distributing malware or malicious content</li>
                  <li>Violating intellectual property rights of others</li>
                  <li>Creating websites that promote harassment or defamation</li>
                  <li>Reselling our services without authorization</li>
                </ul>
                <p className="text-[#A1A1AA] leading-relaxed mt-4">
                  Misuse of our services may result in immediate termination without refund.
                </p>
              </div>

              {/* Section 9 */}
              <div className="mb-10">
                <h2 className="text-2xl font-bold text-[#F5F5F7] mb-4">9. Confidentiality</h2>
                <p className="text-[#A1A1AA] leading-relaxed">
                  Both parties agree to keep confidential any proprietary or sensitive information shared during the engagement. 
                  This includes business information, access credentials, and project details.
                </p>
              </div>

              {/* Section 10 */}
              <div className="mb-10">
                <h2 className="text-2xl font-bold text-[#F5F5F7] mb-4">10. Termination</h2>
                <p className="text-[#A1A1AA] leading-relaxed">
                  Either party may terminate services with written notice as outlined in the service agreement. 
                  Upon termination, you are responsible for payment of all work completed up to the termination date. 
                  See our Refund Policy for details on cancellations.
                </p>
              </div>

              {/* Section 11 */}
              <div className="mb-10">
                <h2 className="text-2xl font-bold text-[#F5F5F7] mb-4">11. Governing Law</h2>
                <p className="text-[#A1A1AA] leading-relaxed">
                  These Terms shall be governed by and construed in accordance with the laws of India. 
                  Any disputes shall be resolved through good-faith negotiation before pursuing legal remedies.
                </p>
              </div>

              {/* Section 12 */}
              <div className="mb-10">
                <h2 className="text-2xl font-bold text-[#F5F5F7] mb-4">12. Changes to Terms</h2>
                <p className="text-[#A1A1AA] leading-relaxed">
                  We reserve the right to modify these Terms at any time. Changes will be posted on this page with an 
                  updated revision date. Continued use of our services after changes constitutes acceptance of the new Terms.
                </p>
              </div>

              {/* Contact Section */}
              <div className="bg-[#111111] border border-white/10 p-8 mt-12">
                <h2 className="text-2xl font-bold text-[#F5F5F7] mb-4">13. Contact Us</h2>
                <p className="text-[#A1A1AA] leading-relaxed mb-6">
                  If you have any questions about these Terms and Conditions, please contact us at:
                </p>
                <div className="space-y-3">
                  <p className="text-[#F5F5F7] font-semibold">Eleven11 Studios</p>
                  <p className="text-[#A1A1AA] text-sm">22-23, Block S, DLF Phase 3, Gurugram, Haryana, India 122002</p>
                  <a 
                    href="mailto:hello@eleven11-studios.com" 
                    className="flex items-center gap-2 text-[#D4AF37] hover:text-[#C5A059] transition-colors"
                  >
                    <Mail className="w-5 h-5" />
                    hello@eleven11-studios.com
                  </a>
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TermsConditionsPage;
